<template>
  <div>
    <div class="block" style="background-color: #fff; margin-top: 25px;">
      <h2 class="">{{ $t('account.edit.account_recent_activities.headers.products') }}</h2>
      <Table :columns="products.columns" :data="products.data">
        <template #productName="{ row }">
          <a :href="`/products/${row.id}`" target="_blank">
            <span>{{ row.product_name }}</span>
          </a>
        </template>
        <template #manufacturerPart="{ row }">
          <ol>
            <div style="margin: 0.63rem 0" class="manufacturers-part px-2" v-for="mp in row.manufacturer_parts"
              :key="mp.id">
              <li>
                <router-link :to="getPartMenuTarget(mp)" target="_blank">
                  {{ `[${mp.part_identity ? mp.part_identity.part_number : ''}]` }} {{ mp.name || mp.chinese_name }}
                </router-link>
              </li>
            </div>
          </ol>
        </template>
      </Table>
      <div class="text-align-right my-2">
        <router-link to="#" @click.native="handleDataDisplayToggle('products')">
          {{ showMoreBtnText('products') }}
        </router-link>
      </div>
    </div>

    <div class="block" style="background-color: #fff; margin-top: 25px;">
      <h2 class="">{{ $t('account.edit.account_recent_activities.headers.quotes') }}</h2>
      <Table :columns="quotes.columns" :data="quotes.data">
        <template #title="{ row }">
          <a :href="`/quotes/${row.id}`" target="_blank">
            <span>{{ row.title }} ({{ row.number }})</span>
          </a>
        </template>
        <template #createdAt="{ row }">
          <span>{{ moment(row.created_at).format('YYYY-MM-DD') }}</span>
        </template>
      </Table>
      <div class="text-align-right my-2">
        <router-link to="#" @click.native="handleDataDisplayToggle('quotes')">
          {{ showMoreBtnText('quotes') }}
        </router-link>
      </div>
    </div>

    <div class="block" style="background-color: #fff; margin-top: 25px;">
      <h2 class="">{{ $t('account.edit.account_recent_activities.headers.orders') }}</h2>
      <Table :columns="orders.columns" :data="orders.data">
        <template #title="{ row }">
          <a :href="`/orders/${row.id}`" target="_blank">
            <span>{{ row.description }} ({{ row.order_number }})</span>
          </a>
        </template>
        <template #createdAt="{ row }">
          <span>{{ moment(row.created_at).format('YYYY-MM-DD') }}</span>
        </template>
        <template #status="{ row }">
          <span>{{ getOrderStatus(row) }}</span>
        </template>
      </Table>
      <div class="text-align-right my-2">
        <router-link to="#" @click.native="handleDataDisplayToggle('orders')">
          {{ showMoreBtnText('orders') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: "AccountRecentActivities",
  props: {
    accountId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      products: {},
      orders: {},
      quotes: {},
      showMoreProducts: false,
      showMoreQuotes: false,
      showMoreOrders: false
    }
  },
  mounted() {
    this.getAccountRecentActivities(this.accountId)
  },
  methods: {
    ...mapActions([
      'getAccountRecentActivitiesSummary'
    ]),
    moment,
    getAccountRecentActivities(accountId) {
      this.getAccountRecentActivitiesSummary(accountId).then(resp => {
        this.setProductTableDetails(resp.data.products)
        this.setOrderTableDetails(resp.data.orders)
        this.setQuoteTableDetails(resp.data.quotes)
      })
    },
    setProductTableDetails(products) {
      let productTableDetails = {
        columns: [],
        data: []
      }

      productTableDetails.columns = [
        {
          title: this.$t('account.edit.account_recent_activities.products.product_name'),
          slot: "productName"
        },
        {
          title: this.$t('account.edit.account_recent_activities.products.manufacturer_parts'),
          slot: "manufacturerPart"
        }
      ]

      productTableDetails.data = products

      this.products = productTableDetails
      if (!this.showMoreProducts) {
        this.chunkData('products')
      }
    },
    setQuoteTableDetails(quotes) {
      let quoteTableDetails = {
        columns: [],
        data: []
      }

      quoteTableDetails.columns = [
        {
          title: this.$t('account.edit.account_recent_activities.order_and_quotes.title'),
          slot: "title"
        },
        {
          title: this.$t('account.edit.account_recent_activities.order_and_quotes.creation_date'),
          slot: "createdAt"
        },
        {
          title: this.$t('account.edit.account_recent_activities.order_and_quotes.status'),
          key: "status"
        }
      ]

      quoteTableDetails.data = quotes

      this.quotes = quoteTableDetails
      if (!this.showMoreQuotes) {
        this.chunkData('quotes')
      }
    },
    setOrderTableDetails(orders) {
      let orderTableDetails = {
        columns: [],
        data: []
      }

      orderTableDetails.columns = [
        {
          title: this.$t('account.edit.account_recent_activities.order_and_quotes.title'),
          slot: "title"
        },
        {
          title: this.$t('account.edit.account_recent_activities.order_and_quotes.creation_date'),
          slot: "createdAt"
        },
        {
          title: this.$t('account.edit.account_recent_activities.order_and_quotes.status'),
          slot: "status"
        }
      ]

      orderTableDetails.data = orders

      this.orders = orderTableDetails
      if (!this.showMoreOrders) {
        this.chunkData('orders')
      }
    },
    getOrderStatus(order) {
      switch (order.order_status) {
        case 0:
          return "Open"
        case 1:
          return "Closed"
        case 2:
          return "Invalid"
        default:
          return ""
      }
    },
    getPartMenuTarget(manufacturerPart) {
      const partType = manufacturerPart.component_type === 'circuit_board' ? 'pcb' : manufacturerPart.component_type
      return { name: `menu.${partType}.edit`, params: { partId: manufacturerPart.id } }
    },
    showMoreBtnText(dataType) {
      switch (dataType) {
        case 'products':
          return this.showMoreProducts ? this.$t('account.edit.account_recent_activities.show_less_data') : this.$t('account.edit.account_recent_activities.show_more_data')
        case 'orders':
          return this.showMoreOrders ? this.$t('account.edit.account_recent_activities.show_less_data') : this.$t('account.edit.account_recent_activities.show_more_data')
        case 'quotes':
          return this.showMoreQuotes ? this.$t('account.edit.account_recent_activities.show_less_data') : this.$t('account.edit.account_recent_activities.show_more_data')
        default:
          break
      }
    },
    chunkData(dataType) {
      switch (dataType) {
        case 'products':
          this.products.data = this.products.data.slice(0, 5)
          break
        case 'orders':
          this.orders.data = this.orders.data.slice(0, 5)
          break
        case 'quotes':
          this.quotes.data = this.quotes.data.slice(0, 5)
          break
        default:
          break
      }
    },
    handleDataDisplayToggle(dataType) {
      switch (dataType) {
        case 'products':
          this.showMoreProducts = !this.showMoreProducts
          this.products.data = !this.showMoreProducts ? this.products.data.slice(0, 5) : this.getAccountRecentActivities(this.accountId)
          break
        case 'orders':
          this.showMoreOrders = !this.showMoreOrders
          this.orders.data = !this.showMoreOrders ? this.orders.data.slice(0, 5) : this.getAccountRecentActivities(this.accountId)
          break
        case 'quotes':
          this.showMoreQuotes = !this.showMoreQuotes
          this.quotes.data = !this.showMoreQuotes ? this.quotes.data.slice(0, 5) : this.getAccountRecentActivities(this.accountId)
          break
        default:
          break
      }
    }
  }

}
</script>
